/**
 * Client goals / care plan reviews. Each client has 2–4 goal "slots"; each
 * slot is a chain of goals (start → review due six months later → signed off
 * at review, then the next goal starts). Unsigned goals past their review date
 * are the overdue reviews. Youth Services runs behind on reviews — the
 * believable problem spot on this page.
 */
import { DATA_START, Quarter, addDays, now, ymd } from '../../clock';
import { rng } from '../../rng';
import { Client, activeClients } from '../../world';
import { careStaff, memo, round1, scopeOf, syncedAt } from './common';

export interface Goal {
    id: number;
    client: Client;
    status: 'active' | 'achieved' | 'discontinued';
    outcome: 'achieved' | 'partial' | 'not_achieved' | null;
    goal: string;
    serviceArea: string;
    capacity: string;
    threeFold: string;
    start: Date;
    review: Date | null;
    signedOff: Date | null;
    supervisor: string;
}

const GOALS: Record<string, string[]> = {
    'Disability Services (NDIS)': [
        'Go out bush with family on weekends',
        'Cook a simple meal with support each week',
        'Use the community store card to shop independently',
        'Attend the day program three times a week',
        'Keep up physio exercises to walk further',
        'Visit family in another community for sorry business with support',
        'Learn to use a tablet to video call family',
    ],
    'Aged Care': [
        'Stay living at home on country with support',
        'Attend the aged care centre twice a week',
        'Keep up daily walks to stay strong',
        'Take medicines on time with Webster-pak prompts',
        'Go on country trips with other elders',
        'Keep in touch with grandchildren',
    ],
    'Ngangkari Program': [
        'Receive regular ngangkari treatment alongside clinic care',
        'Share healing knowledge with young people',
        'Reduce headaches and poor sleep',
    ],
    'Youth Services': [
        'Take part in the school holiday program',
        'Get a learner driver licence',
        'Attend school at least four days a week',
        'Join the community footy team',
        'Complete a work-ready course',
    ],
};

const CAPACITY = ['Daily living', 'Health & wellbeing', 'Social & community participation', 'Relationships', 'Lifelong learning', 'Culture & country'];
const THREE_FOLD = ['Maintain', 'Build capacity', 'Increase independence'];

const goalHistory = memo((): Goal[] => {
    const n = now();
    const out: Goal[] = [];
    let id = 5000;
    for (const client of activeClients()) {
        const r = rng(`goals:client:${client.code}`);
        const slots = r.weighted([[2, 3], [3, 5], [4, 2]] as [number, number][]);
        // Youth Services runs behind on reviews; everyone else is mostly on time.
        const onTime = client.program === 'Youth Services' ? 0.62 : client.program === 'Aged Care' ? 0.9 : 0.85;
        const joined = new Date(client.joined);
        for (let slot = 0; slot < slots; slot++) {
            let start = addDays(new Date(Math.max(joined.getTime(), DATA_START.getTime())), -r.int(10, 200));
            if (start < joined) {
                start = joined;
            }
            for (let guard = 0; guard < 8 && start <= n; guard++) {
                const noReview = r.bool(0.02);
                const review = noReview ? null : addDays(start, r.pick([182, 182, 183, 365]) + r.int(-10, 10));
                let signedOff: Date | null = null;
                if (review && review <= n) {
                    // On time, late, or (rarely) never signed off.
                    const late = r.bool(onTime) ? r.int(-14, 10) : r.bool(0.85) ? r.int(25, 160) : 100000;
                    const candidate = addDays(review, late);
                    signedOff = candidate <= n ? candidate : null;
                }
                const outcome = signedOff ? r.weighted([['achieved', 40], ['partial', 46], ['not_achieved', 14]] as ['achieved' | 'partial' | 'not_achieved', number][]) : null;
                out.push({
                    id: id++,
                    client,
                    status: !signedOff ? 'active' : outcome === 'not_achieved' ? 'discontinued' : outcome === 'achieved' ? 'achieved' : 'active',
                    outcome,
                    goal: r.pick(GOALS[client.program] || GOALS['Disability Services (NDIS)']),
                    serviceArea: client.program,
                    capacity: r.pick(CAPACITY),
                    threeFold: r.pick(THREE_FOLD),
                    start,
                    review,
                    signedOff,
                    supervisor: r.pick(careStaff(client.program, client.site)).name,
                });
                if (!signedOff) {
                    break;
                }
                start = addDays(signedOff, r.int(0, 14));
            }
        }
    }
    return out;
});

export function goalRecords(): Goal[] {
    return goalHistory('all');
}

const GRACE_DAYS = 10;

interface GoalScope {
    list: Goal[];
    from: Date;
    to: Date;
}

/** Goals live during the scope (started before it ended, not signed off before it began). */
function goalsIn(q: Quarter | null): GoalScope {
    const s = scopeOf(q);
    const list = goalRecords().filter(g => g.start <= s.to && (!g.signedOff || g.signedOff >= s.from));
    return { list, from: s.from, to: s.to };
}

/** GET /api/goals/stats. Board twin omits overdue_by_client and goals_by_client. */
export function goalStats(q: Quarter | null, board = false): Record<string, unknown> {
    const { list, from, to } = goalsIn(q);
    const openAtEnd = (g: Goal) => !g.signedOff || g.signedOff > to;
    const overdue = list.filter(g => g.review && g.review < to && openAtEnd(g));
    const reviewed = list.filter(g => g.signedOff && g.signedOff >= from && g.signedOff <= to);
    const noReview = list.filter(g => !g.review && openAtEnd(g));
    // Reviews falling due in the period (after a ten-day grace), and how many
    // were signed off by ten days after the period closed.
    const graceEnd = addDays(now(), -GRACE_DAYS);
    const due = list.filter(g => g.review && g.review >= from && g.review <= to && g.review <= graceEnd);
    const done = due.filter(g => g.signedOff && g.signedOff <= addDays(to, GRACE_DAYS));

    const count = <T>(items: T[], key: (t: T) => string) => {
        const m = new Map<string, number>();
        items.forEach(i => m.set(key(i), (m.get(key(i)) || 0) + 1));
        return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    };

    const out: Record<string, unknown> = {
        total: list.length,
        active: list.filter(g => g.status === 'active' && openAtEnd(g)).length,
        achieved: list.filter(g => g.status === 'achieved').length,
        overdue_reviews: overdue.length,
        reviewed: reviewed.length,
        no_review_date: noReview.length,
        review_compliance_rate: due.length ? round1((done.length / due.length) * 100) : 0.0,
        by_status: count(list, g => g.status).map(([status, c]) => ({ status, count: c })),
        by_service_area: count(list, g => g.serviceArea).map(([serviceArea, c]) => ({ serviceArea, count: c })),
        overdue_by_client: count(overdue, g => g.client.code).map(([client_id, c]) => ({ client_id, count: c })),
        goals_by_client: count(list, g => g.client.code).map(([client_id, c]) => ({ client_id, total: c })),
        last_synced_at: syncedAt('goals'),
    };
    if (board) {
        delete out['overdue_by_client'];
        delete out['goals_by_client'];
    }
    return out;
}

/** GET /api/goals/export. */
export function goalExport(q: Quarter | null): Record<string, unknown> {
    const rows = goalsIn(q).list.map(g => ({
        client: g.client.code,
        webcare_client_id: g.client.id,
        status: g.status,
        goal: g.goal,
        service_area: g.serviceArea,
        capacity: g.capacity,
        three_fold: g.threeFold,
        start_date: ymd(g.start),
        review_date: g.review ? ymd(g.review) : null,
        supervisor: g.supervisor,
        signed_off_date: g.signedOff ? ymd(g.signedOff) : null,
    }));
    return { quarter: q?.key ?? null, count: rows.length, rows };
}

/** Goal outcomes reviewed in scope, for the CEO report's "Client goals progression". */
export function goalProgression(q: Quarter | null): { reviewed: number; achieved: number; partial: number; achievedPct: number; partialPct: number } {
    const { list, from, to } = goalsIn(q);
    const reviewed = list.filter(g => g.signedOff && g.signedOff >= from && g.signedOff <= to);
    const achieved = reviewed.filter(g => g.outcome === 'achieved').length;
    const partial = reviewed.filter(g => g.outcome === 'partial').length;
    const pct = (v: number) => (reviewed.length ? Math.round((v / reviewed.length) * 100) : 0);
    return { reviewed: reviewed.length, achieved, partial, achievedPct: pct(achieved), partialPct: pct(partial) };
}
